import React from 'react';
import { Field, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { AbstractButton, Box, Flex, Text } from 'pounceV2';
import { useTheme } from '@mui/material';

import ErrorBoundaryV3 from 'Components/ErrorBoundary/ErrorBoundaryV3';
import { useGetGeneralSettings } from 'Source/graphql/queries/getGeneralSettings.generated';
import AISummaryPromptInputV3 from './AISummaryPromptFieldV3';
import OverflowAIResponseV3 from './OverflowAIResponseV3';
import {
  getAiOutputLengthSetting,
  RerunFormValues,
  useGenericAISummaryContextV3,
} from './GenericAISummaryContextV3';
import { useAIAttachments } from './hooks';
import { KINDS_SUPPORTING_ATTACHMENTS } from './utils/aiKindUtils';
import PromptActionsV3 from './PromptActionsV3';

const validationSchema = Yup.object().shape({
  additionalPrompt: Yup.string().optional().default('Summarize'),
});

type AIExplainLogEventsProps = {
  placeholder?: string;
  renderContent?: (
    aiSummary: string,
    aiSummaryError: string,
    endContent?: React.ReactNode,
  ) => React.ReactNode;
  stickyInput?: boolean;
  streamId?: string;
  hidePromptDisplay?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
};

const DEFAULT_PLACEHOLDER = 'Ask a clarifying question, run a follow on prompt, etc.';

const RetryEndContent = ({ rerun }: { rerun: (values: RerunFormValues) => void }) => {
  const {
    palette: { tokens, componentTokens },
  } = useTheme();
  const { values } = useFormikContext<RerunFormValues>();

  return (
    <Box fontStyle="italic" color={tokens.content.text.accent[14][3]}>
      Response manually stopped.&nbsp;
      <AbstractButton
        color={componentTokens.buttons.colors.link.primary.text.resting}
        _hover={{ color: componentTokens.buttons.colors.link.primary.text.hover }}
        onClick={e => {
          e.preventDefault();
          rerun(values);
        }}
      >
        Retry
      </AbstractButton>
      ?
    </Box>
  );
};

const InitialPromptDisplay = ({ prompt }: { prompt: string }) => {
  if (!prompt) {
    return null;
  }

  return (
    <Flex mt={4} align="center" gap={2}>
      <Text
        maxHeight="108px"
        fontSize="large"
        fontStyle="italic"
        fontWeight="bold"
        flex={1}
        wordBreak="break-word"
        whiteSpace="pre-wrap"
      >
        {prompt}
      </Text>
      <Box flexShrink={0}>
        <PromptActionsV3 prompt={prompt} />
      </Box>
    </Flex>
  );
};

const AIExplainLogEventsV3 = ({
  placeholder = DEFAULT_PLACEHOLDER,
  renderContent,
  stickyInput,
  hidePromptDisplay = false,
  autoFocus = false,
  disabled = false,
}: AIExplainLogEventsProps) => {
  const {
    palette: { tokens },
  } = useTheme();
  const {
    rerun,
    alias,
    aiSummary,
    aiSummaryError,
    isLoading,
    cancelPrompt,
    isCancelled,
    initialUserPrompt,
    kind,
    usage,
    riskScoreEvidence,
  } = useGenericAISummaryContextV3();

  const { data: settingsData } = useGetGeneralSettings();
  const isWebAccessEnabled = settingsData?.generalSettings?.pantherAIWebTool?.enabled;
  const supportsAttachments = isWebAccessEnabled && KINDS_SUPPORTING_ATTACHMENTS.has(kind);

  const {
    attachments,
    attachmentUrls,
    addAttachment,
    removeAttachment,
    clearAttachments,
    canAttach,
    isUploading,
  } = useAIAttachments();

  const retryEndContent: React.ReactNode = React.useMemo(() => {
    if (isCancelled) {
      return <RetryEndContent rerun={rerun} />;
    }
    return undefined;
  }, [isCancelled, rerun]);

  const initialValues = React.useMemo(() => {
    return {
      additionalPrompt: '',
      kind,
      outputLength: getAiOutputLengthSetting(kind),
    };
  }, [kind]);

  return (
    <Box width="100%">
      <ErrorBoundaryV3>
        <Formik<RerunFormValues>
          initialValues={initialValues}
          onSubmit={(values, { resetForm }) => {
            rerun(values, attachmentUrls);
            clearAttachments();
            resetForm({ values: { ...values, additionalPrompt: '' } });
          }}
          validationSchema={validationSchema}
        >
          <Box data-tid="event-ai-form">
            <Box position="relative">
              <Box
                {...(stickyInput && {
                  position: 'sticky',
                  top: 115,
                  zIndex: 1,
                  backgroundColor: tokens.background.neutral.canvas.base,
                  boxShadow: `0px 12px 10px 12px ${tokens.background.neutral.canvas.base}`,
                })}
              >
                <Field
                  data-tid="ai-additional-prompt"
                  as={AISummaryPromptInputV3}
                  alias={alias}
                  isLoading={isLoading}
                  cancelPrompt={cancelPrompt}
                  placeholder={placeholder}
                  name="additionalPrompt"
                  autoFocus={autoFocus}
                  disabled={disabled}
                  onAttachFile={supportsAttachments ? addAttachment : undefined}
                  isAttachDisabled={!canAttach}
                  isUploading={isUploading}
                  attachments={supportsAttachments ? attachments : undefined}
                  onRemoveAttachment={supportsAttachments ? removeAttachment : undefined}
                />
              </Box>
              <Box position="relative" pt={2}>
                {!hidePromptDisplay && <InitialPromptDisplay prompt={initialUserPrompt} />}
                {renderContent ? (
                  renderContent(aiSummary, aiSummaryError, retryEndContent)
                ) : (
                  <>
                    {/* We treat the error as a summary, we want to show it in the response */}
                    {(aiSummary || aiSummaryError) && (
                      <OverflowAIResponseV3
                        value={aiSummary || aiSummaryError}
                        aria-label="AI Summary"
                        id="event-explanation"
                        isLoading={isLoading}
                        usage={usage}
                        endContent={retryEndContent}
                        riskScoreEvidence={riskScoreEvidence}
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Formik>
      </ErrorBoundaryV3>
    </Box>
  );
};

export default React.memo(AIExplainLogEventsV3);
